// src/components/programs/ProgramForm/ProgramFormWrapper.jsx

import React, { useEffect } from "react";
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Paper,
    CircularProgress,
    Typography,
    Stack,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { format } from "date-fns";
import useFormLanguage from "../../../hooks/forms/useFormLanguage";

// Import steps
import Step0ProgramInfo from "./Step0ProgramInfo";
import Step1Hotels from "./Step1Hotels";
import Step1ProgramData from "./Step1ProgramData";
import Step2Quotation from "./Step2Quotation";

// Import hooks
import useProgramForm from "../../../hooks/programs/useProgramForm";
import useCreateProgram from "../../../hooks/programs/useCreateProgram";
import useUpdateProgram from "../../../hooks/programs/useUpdateProgram";

const ProgramFormWrapper = ({ isEdit = false, currentData = null }) => {
    const navigate = useNavigate();
    const { isArabic, activeLang, setActiveLang } = useFormLanguage();

    // Form hook with draft support
    const {
        currentStep,
        formData,
        setFormData,
        errors,
        setErrors,
        validateStep,
        handleNext,
        handleBack,
        buildPayload,
        hasDraft,
        draftTimestamp,
        isRestoredFromDraft,
        lastSavedAt,
        clearDraft,
        discardDraft,
    } = useProgramForm(isEdit, currentData, isArabic);

    // Mutations
    const { createProgramMutate, isPending: isCreating } = useCreateProgram(() => {
        clearDraft();
        navigate("/programs");
    });

    const { updateProgramMutate, isPending: isUpdating } = useUpdateProgram(() => {
        clearDraft();
        navigate("/programs");
    });

    // Notify via toast when draft is restored on initial load
    useEffect(() => {
        if (isRestoredFromDraft) {
            toast.custom(
                (t) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            px: 2,
                            bgcolor: (theme) => (theme.palette.mode === "dark" ? "#1e293b" : "#ffffff"),
                            color: (theme) => (theme.palette.mode === "dark" ? "#f8fafc" : "#1e293b"),
                            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                            borderRadius: 2,
                            border: "1px solid #38bdf8",
                            direction: isArabic ? "rtl" : "ltr",
                        }}
                    >
                        <RestoreIcon sx={{ color: "#0284c7" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {isArabic
                                ? `تمت استعادة المسودة المحفوظة تلقائيًا${
                                      draftTimestamp
                                          ? ` (${format(new Date(draftTimestamp), "hh:mm a")})`
                                          : ""
                                  }`
                                : `Autosaved draft restored${
                                      draftTimestamp
                                          ? ` (${format(new Date(draftTimestamp), "hh:mm a")})`
                                          : ""
                                  }`}
                        </Typography>
                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => {
                                toast.dismiss(t.id);
                                discardDraft();
                            }}
                            sx={{
                                ml: isArabic ? 0 : 1,
                                mr: isArabic ? 1 : 0,
                                textTransform: "none",
                                fontSize: "0.75rem",
                                py: 0.25,
                                px: 1,
                                minWidth: "auto",
                            }}
                        >
                            {isArabic ? "البدء من جديد" : "Start Fresh"}
                        </Button>
                    </Box>
                ),
                { id: "draft-restored-toast", duration: 5000, position: isArabic ? "top-left" : "top-right" }
            );
        }
    }, [isRestoredFromDraft, draftTimestamp, isArabic, discardDraft]);

    // Callback to clear errors from parent when user edits fields
    const handleClearErrors = React.useCallback((fieldNames) => {
        const newErrors = { ...errors };
        fieldNames.forEach(field => {
            delete newErrors[field];
        });
        setErrors(newErrors);
    }, [errors, setErrors]);

    // Steps configuration
    const steps = [
        { label: isArabic ? "معلومات البرنامج" : "Program Info" },
        { label: isArabic ? "الفنادق" : "Hotels" },
        { label: isArabic ? "بيانات البرنامج" : "Program Data" },
        { label: isArabic ? "التسعير" : "Quotation" },
    ];

    /**
     * Handle submit
     */
    const handleSubmit = () => {
        // ✅ Validate before submitting
        if (!validateStep(currentStep, formData, activeLang)) {
            toast.error(
                isArabic
                    ? "الرجاء تعبئة جميع الحقول المطلوبة بشكل صحيح"
                    : "Please fill all required fields correctly"
            );
            return;
        }

        const payload = buildPayload();

        if (isEdit) {
            updateProgramMutate(
                { id: currentData.id, req: payload },
                {
                    onSuccess: () => {
                        clearDraft();
                    },
                }
            );
        } else {
            createProgramMutate(payload, {
                onSuccess: () => {
                    clearDraft();
                },
            });
        }
    };

    const handleCancel = () => {
        if (hasDraft) {
            toast.custom(
                (t) => (
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: (theme) => (theme.palette.mode === "dark" ? "#1e293b" : "#ffffff"),
                            color: (theme) => (theme.palette.mode === "dark" ? "#f8fafc" : "#1e293b"),
                            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                            borderRadius: 2,
                            border: "1px solid #cbd5e1",
                            maxWidth: 400,
                            direction: isArabic ? "rtl" : "ltr",
                        }}
                    >
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                            {isArabic
                                ? "هل تريد العودة؟ مسودتك محفوظة تلقائياً ويمكنك إكمالها في أي وقت."
                                : "Exit? Your draft is autosaved so you can continue later."}
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => toast.dismiss(t.id)}
                            >
                                {isArabic ? "البقاء" : "Stay"}
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    toast.dismiss(t.id);
                                    navigate("/programs");
                                }}
                            >
                                {isArabic ? "العودة للقائمة" : "Exit"}
                            </Button>
                        </Stack>
                    </Box>
                ),
                { id: "cancel-draft-confirm", duration: 6000, position: "top-center" }
            );
        } else {
            navigate("/programs");
        }
    };

    /**
     * Render step content
     */
    const renderStepContent = ({ activeLang, setActiveLang } = {}) => {
        switch (currentStep) {
            case 0:
                return (
                    <Step0ProgramInfo
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        isEdit={isEdit}
                        currentData={currentData}
                        onClearErrors={handleClearErrors}
                        activeLang={activeLang}
                        setActiveLang={setActiveLang}
                        isArabic={isArabic}
                    />
                );
            case 1:
                return (
                    <Step1Hotels
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        isEdit={isEdit}
                        currentData={currentData}
                        activeLang={activeLang}
                        isArabic={isArabic}
                    />
                );
            case 2:
                return (
                    <Step1ProgramData
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        isEdit={isEdit}
                        currentData={currentData}
                        activeLang={activeLang}
                        setActiveLang={setActiveLang}
                    />
                );
            case 3:
                return (
                    <Step2Quotation
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        isEdit={isEdit}
                        currentData={currentData}
                        activeLang={activeLang}
                    />
                );
            default:
                return null;
        }
    };

    const isLoading = isCreating || isUpdating;

    return (
        <Box>
            {/* Stepper with Autosave Indicator & Actions */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        {steps[currentStep]?.label}
                    </Typography>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                        {lastSavedAt ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "success.main" }} />
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {isArabic
                                        ? `تم الحفظ تلقائيًا ${format(new Date(lastSavedAt), "hh:mm:ss a")}`
                                        : `Autosaved ${format(new Date(lastSavedAt), "hh:mm:ss a")}`}
                                </Typography>
                            </Stack>
                        ) : hasDraft ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "info.main" }} />
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {isArabic ? "مسودة محفوظة" : "Draft saved"}
                                </Typography>
                            </Stack>
                        ) : null}

                        {hasDraft && (
                            <Button
                                size="small"
                                color="error"
                                onClick={discardDraft}
                                sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.2, px: 1, minWidth: "auto" }}
                            >
                                {isArabic ? "حذف المسودة" : "Discard Draft"}
                            </Button>
                        )}
                    </Stack>
                </Box>

                <Stepper activeStep={currentStep}>
                    {steps.map((step, index) => (
                        <Step key={index}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            {/* Step Content */}
            <Box sx={{ mb: 3 }}>
                {renderStepContent({ activeLang, setActiveLang })}
            </Box>

            {/* Navigation Buttons */}
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Button
                        disabled={currentStep === 0 || isLoading}
                        onClick={handleBack}
                        variant="outlined"
                    >
                        {isArabic ? "السابق" : "Back"}
                    </Button>

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            {isArabic ? "إلغاء" : "Cancel"}
                        </Button>

                        {currentStep === steps.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : null}
                            >
                                {isLoading
                                    ? (isArabic ? "جارٍ الحفظ..." : "Saving...")
                                    : isEdit
                                        ? (isArabic ? "تحديث" : "Update")
                                        : (isArabic ? "حفظ" : "Save")}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={() => handleNext(activeLang)}
                                disabled={isLoading}
                            >
                                {isArabic ? "التالي" : "Next"}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ProgramFormWrapper;
